import { getGenericEventPropertiesObject, InstrumentationEventPropertyInterface } from "sh-instrumentation";
import { UserStorageKeys } from "sh-models";
import ECSConfigKey from "./ecsconfigservice/ECSConfigKey";
import ECSConfigService from "./ecsconfigservice/ECSConfigService";
import InstrumentationService from "./InstrumentationService/InstrumentationService";
import UserStorageService from "./UserStorageService";

/**
 * Per-user map of schedule (keyed by teamId) -> published-before flag.
 * A user can own multiple schedules, so each is tracked independently. Only published
 * schedules are stored (value is always `true`); absence of a key means "never published".
 * The map is persisted via UserStorageService's default CurrentUser scope, so it is
 * automatically namespaced per user (no cross-user bleed on shared devices).
 */
export type PublishedScheduleMap = { [teamId: string]: true };

/**
 * ECS kill-switch for the ScheduleHasNeverBeenPublished telemetry. When disabled, the
 * read/stamp/write flow is a no-op so no localStorage entries are created or read.
 */
function isScheduleNeverPublishedTelemetryEnabled(): boolean {
    return ECSConfigService.isECSFeatureEnabled(ECSConfigKey.ScheduleNeverPublishedTelemetryEnabled);
}

function getPublishedScheduleMap(): PublishedScheduleMap {
    return UserStorageService.getItem<PublishedScheduleMap>(UserStorageKeys.PublishedScheduleMap) || {};
}

/**
 * READ: returns true when the given schedule (teamId) has never been published before.
 * Pure (not ECS-gated) so callers and tests can reason about it directly; when the ECS
 * flag is off the value simply never gets stamped onto an event (see stampScheduleNeverBeenPublished).
 * Returns false for a missing teamId (cannot claim "never" without an id — safe default for metrics).
 */
export function hasScheduleNeverBeenPublished(teamId: string): boolean {
    if (!teamId) {
        return false;
    }
    return !getPublishedScheduleMap()[teamId];
}

/**
 * WRITE: marks the given schedule (teamId) as published. Idempotent and ECS-gated.
 * Call AFTER stamping the event so the first publish of a schedule reports true and later ones false.
 */
export function markSchedulePublished(teamId: string): void {
    if (!isScheduleNeverPublishedTelemetryEnabled() || !teamId) {
        return;
    }
    const map = getPublishedScheduleMap();
    if (!map[teamId]) {
        map[teamId] = true;
        UserStorageService.setItem(UserStorageKeys.PublishedScheduleMap, map);
    }
}

/**
 * Pushes the ScheduleHasNeverBeenPublished property onto the given event-properties array,
 * reading the PRE-write map so the first publish reports true. ECS-gated (no-op when disabled).
 * Must be called before logEvent, and before markSchedulePublished, for correct ordering.
 *
 * Best-effort / non-blocking: this stamp runs inline before callers log their event and, for
 * AddShift/AddTimeOff, before they launch the Add/Edit panel. The underlying user-storage read
 * (or the ECS lookup) could theoretically throw, so the whole body is guarded — a failure here
 * simply skips the stamp and must never block or fail the caller. Callers therefore do not need
 * their own try/catch around this function.
 */
export function stampScheduleNeverBeenPublished(eventProperties: InstrumentationEventPropertyInterface[], teamId: string): void {
    try {
        if (!isScheduleNeverPublishedTelemetryEnabled()) {
            return;
        }
        eventProperties.push(getGenericEventPropertiesObject(
            InstrumentationService.properties.ScheduleHasNeverBeenPublished,
            hasScheduleNeverBeenPublished(teamId)));
    } catch (e) {
        // Best-effort telemetry: swallow storage/ECS read failures so we never block the caller
        // (e.g. launching the Add/Edit shift panel or completing a share).
    }
}
